import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkedAlt, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'
import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons'
import camelcaseKeys from 'camelcase-keys'
import cx from 'classnames'
import isNil from 'lodash/isNil'
import React, { useState } from 'react'
import PlacesAutocomplete, {
  Suggestion,
  geocodeByAddress,
  geocodeByPlaceId,
  getLatLng,
  PropTypes as PlacesAutocompleteProps
} from 'react-places-autocomplete'
import { useMutation } from '@tanstack/react-query'
import { GeocoderResult, AddressPoint, Location, googleGeocodeToLocalGeocode } from '@commutifi-fe/interfaces'
import { computeBounds, locationToBounds } from '@commutifi-fe/map/utils'
import { FitBounds, Map, Marker, MapMouseEvent, MapStyles } from '@commutifi-fe/map'
import { colors } from '@commutifi-fe/ui/styles/constants'
import { useBrowserLocation } from '@commutifi-fe/hooks'
import { getArrayValue } from '@commutifi-fe/utils'
import { logger } from '@commutifi-fe/logger'
import {
  BrandedLoading,
  Typography,
  Button,
  EmptyDropdown,
  Input,
  Popover as AppPopover,
  Spin,
  AutoComplete,
  AutoCompleteProps,
  useLocaleReceiver
} from '@commutifi-fe/ui'
import MapCircleMarker from './circle-marker.png'
import MapPlaceMarker from './place-marker.png'
import styles from './styles.module.scss'
import { Themes } from '@commutifi-fe/ui/components/theme/constants'

/**
 * TODO refactor: it doesn't make sense to depend on window.google
 * Calls google api geocoder service with reversed geocoding parameters (lat. lnt)
 * and wrap it in a Promise
 * @param lat - latitude coordinate
 * @param lng - longitude coordinate
 *
 * @returns Google geocode results or empty array if it returns an error ZERO_RESULTS
 */
export const geocodeByLatLng = (lat: number, lng: number): Promise<google.maps.GeocoderResult[] | null> => {
  const geocoder = new window.google.maps.Geocoder()
  const geocoderStatus = window.google.maps.GeocoderStatus

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      {
        location: new window.google.maps.LatLng(lat, lng)
      },
      (results, status) => {
        if (status !== geocoderStatus.OK) {
          status === geocoderStatus.ZERO_RESULTS ? resolve([]) : reject(status)
        }
        resolve(results)
      }
    )
  })
}

export enum AutocompleteErrorsEnum {
  NoResultsError = 'NO_RESULTS ERROR',
  FetchingError = 'FETCHING ERROR',
  GeocodeError = 'GEOCODE ERROR'
}
export type AutocompleteErrorCodes = `${AutocompleteErrorsEnum}`

interface AddressAutocompleteProps extends Omit<AutoCompleteProps, 'onSelect' | 'placeholder'> {
  onSelect?: (option: GeocoderResult | null) => void
  onError?: (errorCode: AutocompleteErrorCodes) => void
  value?: string
  // Initial location to set on autocomplete Map component
  initialLocation?: Location
  // Location to use to target better autocomplete results
  locationBias?: Location
  placeholder?: string | null
  isInteractive?: boolean
}

interface AutocompleteMapProps {
  // start and end locations to fit the maps to those bounds
  // and render markers to identify those points
  startLocation: Location | null
  endLocation?: Location | null
  // set extra points to consider in the map bounds. Those location
  // won't generate any markers on the map compared to start and end locations
  locationBias?: Location | Location[]
  theme?: Themes | `${Themes}`
}

interface AddressAutocompleteContextInterface {
  waypointLocation: Location | undefined
  onLocationClick: (location: Location) => Promise<void>
  loading: boolean
}

interface AddressAutoCompleteFormInputProps extends Omit<AddressAutocompleteProps, 'value' | 'onChange'> {
  value?: AddressPoint
  onChange?: (address: AddressPoint | null) => void
}

// Thanks to Kent C. Dodds for the inspiration:
// https://kentcdodds.com/blog/compound-components-with-react-hooks
const AddressAutocompleteContext = React.createContext<AddressAutocompleteContextInterface | null>(null)

function InputIcon() {
  return <FontAwesomeIcon icon={faMapMarkerAlt} data-testid="autocomplete-map-marker-suffix" />
}

/**
 * Address autocomplete input that uses google's components and provides a good
 * UX to select an address.
 *
 * NOTE: if you're working on a form
 */
export function AddressAutocomplete({
  placeholder,
  value,
  initialLocation,
  locationBias,
  onSelect,
  onError,
  children,
  isInteractive = true,
  ...rest
}: AddressAutocompleteProps) {
  const intl = useLocaleReceiver('AddressAutocomplete')
  const [inputValue, setInputValue] = useState(value || '')
  const [waypointLocation, setWaypointLocation] = useState(initialLocation)
  const { isPending: loadingGeocode, mutateAsync: callGeocode } = useMutation({
    mutationFn: async (results: google.maps.GeocoderResult[] | null) => {
      if (!results) {
        return
      }
      const res = results[0]
      setInputValue(res.formatted_address)

      // Having a lot of trouble to properly type nested keys to camel with google geocode result
      const formattedRes = camelcaseKeys(res as Record<string, any>, {
        deep: true
      }) as unknown as GeocoderResult
      if (onSelect) {
        onSelect(formattedRes)
      }

      const location = await getLatLng(res)
      setWaypointLocation(location)
    }
  })

  // Handle location bias
  const { data: browserLocation } = useBrowserLocation({
    shouldFetch: !locationBias && isInteractive
  })
  const userLocation = React.useMemo(() => locationBias || browserLocation, [locationBias, browserLocation])

  const handleChange = (address: string) => {
    setInputValue(address)
  }

  const callOnError = React.useCallback(
    (errorCode: AutocompleteErrorsEnum) => {
      if (onError) {
        onError(errorCode)
      }
    },
    [onError]
  )

  const handleLocationChange = React.useCallback(
    async (location: Location) => {
      // Reverse geocode lat,lng to get a formatted address
      try {
        const results = await geocodeByLatLng(location.lat, location.lng)
        await callGeocode(results)
      } catch (error) {
        logger.error('Google reverse geocode api error', error)
        callOnError(AutocompleteErrorsEnum.GeocodeError)
      }
    },
    [callGeocode, callOnError]
  )

  const handleSelect = async (address: string, placeId: string | null) => {
    if (!address && !placeId) {
      setWaypointLocation(undefined)
      if (onSelect) {
        onSelect(null)
      }
      return
    }

    try {
      // Prioritize geocode by address to have more precision like the street number
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Legacy
      const results = address ? await geocodeByAddress(address) : await geocodeByPlaceId(placeId!)

      await callGeocode(results)
    } catch (error) {
      if (error === 'ZERO_RESULTS') {
        callOnError(AutocompleteErrorsEnum.NoResultsError)
      } else {
        callOnError(AutocompleteErrorsEnum.GeocodeError)
      }
    }
  }

  const handlePlaceAutocompleteError = (status: string, clearSuggestions: () => void) => {
    if (status === 'ZERO_RESULTS' && inputValue.length > 15) {
      // We will still try to geocode on blur and then an error will be displayed if needed
      // 15 is just a number we picked that we thought would be good but it's based on nothing
      return
    }
    clearSuggestions()
    callOnError(AutocompleteErrorsEnum.FetchingError)
  }

  /**
   * It's ok to expand type to add locationBias even if the library doesn't define it cause they pass
   * all the searchOptions down to the google places api
   */
  const searchOptions = React.useMemo<
    PlacesAutocompleteProps['searchOptions'] & {
      locationBias?: google.maps.LatLng | google.maps.LatLngBounds
    }
  >(
    () =>
      userLocation
        ? {
            locationBias: locationToBounds(userLocation)
          }
        : {},
    [userLocation]
  )

  const contextValue = React.useMemo(
    () => ({
      waypointLocation,
      onLocationClick: handleLocationChange,
      loading: loadingGeocode
    }),
    [handleLocationChange, loadingGeocode, waypointLocation]
  )

  return (
    <div id="places-autocomplete">
      <PlacesAutocomplete
        ref={(c) => {
          // https://github.com/hibiken/react-places-autocomplete/issues/233#issuecomment-443839879
          if (!c) return
          // @ts-expect-error -- Legacy
          c.handleInputOnBlur = () => {
            /* empty */
          }
        }}
        onChange={handleChange}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Legacy
        onSelect={handleSelect}
        value={inputValue}
        searchOptions={searchOptions}
        onError={handlePlaceAutocompleteError}
        shouldFetchSuggestions={isInteractive}
      >
        {({ getSuggestionItemProps, getInputProps, suggestions, loading: loadingSuggestions }) => {
          const { onChange, ...inputProps } = getInputProps({
            id: 'address-input',
            onBlur: () => {
              // If there is a value selected but the search term changed but the user
              // did not select any new option we want to set the input to the current value.
              // 15 is just a number we picked that we thought would be good but it's based on nothing
              if ((value && value !== inputValue) || (inputValue.length > 15 && suggestions.length === 0)) {
                handleSelect(inputValue, null)
              }
            }
          })

          return (
            <AppPopover
              open={rest.disabled || !isInteractive ? undefined : false}
              theme="dark"
              content={
                <div className="u-padding-vertical--small-2 u-padding-horizontal--medium">
                  {intl.formatMessage({ id: 'disabledPopoverContent' })}
                </div>
              }
            >
              <AutoComplete
                className={cx(
                  'app-address-autocomplete',
                  'app-input',
                  inputValue && 'app-address-autocomplete--has-value'
                )}
                popupClassName="app-address-autocomplete-dropdown"
                style={{ padding: 0 }}
                onChange={(value: string) => {
                  // Fake event cause antd autocomplete send a string and
                  // react-places-autocomplete expects an event with the
                  // updated value. This allows us to use antd AutoComplete dropdown
                  // instead of implementing our own
                  const event = { target: { value } }
                  onChange(event)
                }}
                defaultActiveFirstOption={false}
                notFoundContent={
                  <span translate="no">
                    {loadingSuggestions ? (
                      <div className="u-padding-vertical--small" data-testid="autocomplete-loading">
                        <BrandedLoading
                          label={intl.formatMessage({ id: 'global.loading' })}
                          color={colors.primary400}
                        />
                      </div>
                    ) : suggestions.length === 0 ? (
                      inputProps.value === '' ? (
                        <div className="app-address-autocomplete-option">
                          <Typography.Text type="secondary">
                            {intl.formatMessage({ id: 'emptySearchHint' })}
                          </Typography.Text>
                        </div>
                      ) : (
                        <EmptyDropdown className="u-margin-vertical--small" />
                      )
                    ) : null}
                  </span>
                }
                options={suggestions.map((suggestion: Suggestion) => ({
                  value: suggestion.description,
                  label: (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className: cx(
                          'app-address-autocomplete-option',
                          suggestion.active && 'ant-select-dropdown-menu-item-active'
                        )
                      })}
                    >
                      {suggestion.description}
                    </div>
                  )
                }))}
                {...inputProps}
                {...rest}
              >
                <Input
                  placeholder={placeholder ?? undefined}
                  aria-label="Address Search Autocomplete"
                  data-testid="address-autocomplete"
                  suffix={
                    loadingGeocode ? (
                      <FontAwesomeIcon icon={faSpinnerThird} spin color={colors.primary400} />
                    ) : (
                      <InputIcon />
                    )
                  }
                />
              </AutoComplete>
            </AppPopover>
          )
        }}
      </PlacesAutocomplete>
      <AddressAutocompleteContext.Provider value={contextValue}>{children}</AddressAutocompleteContext.Provider>
    </div>
  )
}

const useAddressAutocompleteContext = () => {
  const context = React.useContext(AddressAutocompleteContext)
  if (!context) {
    throw new Error('Content compound components cannot be rendered outside the SelectableCardGrid component')
  }
  return context
}

const DEFAULT_ZOOM: number = 12
const AutocompleteMap: React.FC<AutocompleteMapProps> = React.memo(
  ({ startLocation, endLocation, locationBias, theme }) => {
    const intl = useLocaleReceiver('AddressAutocomplete')
    const { waypointLocation, onLocationClick, loading } = useAddressAutocompleteContext()
    const [isOpened, setIsOpened] = useState(false)
    const [isMapSelection, setIsMapSelection] = useState(false)

    const mapRef = React.useRef<HTMLDivElement>(null)
    React.useLayoutEffect(() => {
      if (mapRef.current && isOpened) {
        mapRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, [isOpened])

    const handleOpenMap = () => {
      if (!isOpened) {
        setIsOpened(true)
      }
    }

    const handleCloseMap = () => {
      setIsOpened(false)
    }

    const bounds: FitBounds | undefined = React.useMemo(() => {
      if (isMapSelection) {
        setIsMapSelection(false)
        return undefined
      }

      const locationPoints = [
        ...getArrayValue<Location>(startLocation),
        ...getArrayValue<Location>(endLocation),
        ...getArrayValue<Location>(locationBias)
      ].filter((location) => !isNil(location))
      if (waypointLocation) {
        locationPoints.push(waypointLocation)
      }

      return computeBounds(window.mapboxgl, locationPoints)?.computedBounds
    }, [waypointLocation, startLocation, endLocation, locationBias, isMapSelection])

    const handleMapClick = (mapMouseEvent: MapMouseEvent) => {
      setIsMapSelection(true)
      onLocationClick({
        lat: mapMouseEvent.lngLat.lat,
        lng: mapMouseEvent.lngLat.lng
      })
    }

    const renderCloseMapButton = () => (
      <div className={styles['map-close-button']}>
        <Button shape="circle" onClick={handleCloseMap} className="u-flex-align-center u-flex-justify-center">
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </div>
    )

    const renderSelectOnMapButton = () => (
      <Button type="link" size="small" style={{ padding: 0, lineHeight: 'normal' }} onClick={handleOpenMap}>
        <span className={`u-color--${isOpened ? 'neutral-600' : 'primary'} u-font-weight-semi`}>
          <FontAwesomeIcon icon={faMapMarkedAlt} className="u-margin-right--small" />
          {isOpened ? intl.formatMessage({ id: 'mapClickToSetLocation' }) : intl.formatMessage({ id: 'mapSelect' })}
        </span>
      </Button>
    )

    const renderMarker = (coordinates: number[], imgSrc: string, className: string) => (
      <Marker longitude={coordinates[0]} latitude={coordinates[1]} anchor="center">
        <img src={imgSrc} className={className} alt="map marker" />
      </Marker>
    )

    return (
      <div className={styles['map-selector-container']}>
        <span
          data-testid={waypointLocation ? `${waypointLocation.lat}_${waypointLocation.lng}` : 'no-waypoint-on-map'}
        />
        {renderSelectOnMapButton()}
        {isOpened ? (
          <Spin spinning={loading}>
            <div
              ref={mapRef}
              className={cx('u-margin-vertical--small', styles['map-container'])}
              data-testid="autocomplete-map-container"
            >
              {renderCloseMapButton()}
              {/* Couldn't mock the map component inside this component (even if we mocked the Map component from our UI components)
             so we do this to avoid annoying errors in tests */}
              {process.env.NODE_ENV !== 'test' && (
                <div className={styles.map}>
                  <Map
                    mapboxAccessToken={process.env.MAPBOX_API_KEY}
                    onClick={handleMapClick}
                    initialViewState={{
                      bounds,
                      fitBoundsOptions: {
                        padding: { top: 30, bottom: 30, left: 30, right: 30 }
                      },
                      zoom: DEFAULT_ZOOM
                    }}
                    mapStyle={theme === Themes.DARK ? MapStyles.Dark : MapStyles.Street12}
                  >
                    {startLocation
                      ? renderMarker(
                          [startLocation.lng, startLocation.lat],
                          MapCircleMarker as unknown as string,
                          styles['map-circle-marker']
                        )
                      : undefined}
                    {waypointLocation
                      ? renderMarker(
                          [waypointLocation.lng, waypointLocation.lat],
                          MapPlaceMarker as unknown as string,
                          styles['map-place-marker']
                        )
                      : undefined}
                    {endLocation
                      ? renderMarker(
                          [endLocation.lng, endLocation.lat],
                          MapCircleMarker as unknown as string,
                          styles['map-circle-marker']
                        )
                      : undefined}
                  </Map>
                </div>
              )}
            </div>
          </Spin>
        ) : null}
      </div>
    )
  }
)
AutocompleteMap.displayName = 'AutocompleteMap'

/**
 * Wrapper around AppAddressAutoComplete that can be used inside a Form.Item component
 */
export function AddressAutoCompleteFormInput({
  onChange,
  value,
  placeholder,
  children,
  ...autocompleteProps
}: AddressAutoCompleteFormInputProps) {
  const intl = useLocaleReceiver('AddressAutocomplete')
  const handleSelectAddress = (address: GeocoderResult | null) => {
    if (onChange) {
      if (!address) {
        onChange(null)
        return
      }
      onChange(googleGeocodeToLocalGeocode(address))
    }
  }
  return (
    <AddressAutocomplete
      placeholder={placeholder || intl.formatMessage({ id: 'minimalAddressPlaceholder' })}
      onSelect={handleSelectAddress}
      value={value?.formattedAddress}
      initialLocation={value?.location}
      {...autocompleteProps}
    >
      {children}
    </AddressAutocomplete>
  )
}

AddressAutoCompleteFormInput.Map = AutocompleteMap
AddressAutocomplete.Map = AutocompleteMap
export default AddressAutocomplete
